import { DeployPage } from "config/BaseConfig";
import i18n from "i18next";

export enum PodCode {
  DEFAULT = "default",
  TYO101 = "tyo101",
  OGN101 = "ogn101",
}

export const NG_TENANT_CODES = [
  "admin",
  "console",
  "account",
  "simulator",
  "payment",
  "registration",
  "testcall",
  "speedtest",
  "pbx",
  "api",
  "zoom-integration",
  "google-meet-integration",
  "phone",
  "video-integration",
  "developer",
  "developers",
  // {pod_code}-{env}。例 ogn101-dev, tyo101-stg など
  ...Object.values(PodCode)
    .filter((code) => code !== PodCode.DEFAULT)
    .map((code) => `${code}-dev`),
  ...Object.values(PodCode)
    .filter((code) => code !== PodCode.DEFAULT)
    .map((code) => `${code}-stg`),
  ...Object.values(PodCode)
    .filter((code) => code !== PodCode.DEFAULT)
    .map((code) => `${code}-prod`),
];

export const NG_TENANT_CODE_PATTERNS = [
  /^.*miitel.*$/,
  /^.*-api$/,
  /^.*softphone.*$/,
  /^.*e2e.*$/,
  /^.*revcomm.*$/,
  /^.*demo.*$/,
  /^ogn.*$/,
  /^tyo.*$/,
  /^jkt.*$/,
];

export const isValidTenantCode = (input: string): boolean => {
  const pattern = /^[a-z0-9-]+$/;
  if (!pattern.test(input)) {
    return false;
  }

  // NG文字列と一致するかチェック
  if (NG_TENANT_CODES.includes(input)) {
    return false;
  }

  // NG正規表現と一致するかチェック
  for (const ng_tenant_pattern of NG_TENANT_CODE_PATTERNS) {
    if (ng_tenant_pattern.test(input)) {
      return false;
    }
  }
  return true;
};

export interface TenantCodeValidationResult {
  isValid: boolean;
  errorType?: 'format' | 'length' | 'ng_word';
  ngWord?: string;
  errorMessage?: string;
}

export const validateTenantCodeWithDetails = (input: string): TenantCodeValidationResult => {
  // 長さチェック
  if (input.length < 5 || input.length > 32) {
    return {
      isValid: false,
      errorType: 'length',
      errorMessage: i18n.t("register_form_mm.error.messages.tenant_code_length") as string
    };
  }

  // フォーマットチェック
  const pattern = /^[a-z0-9-]+$/;
  if (!pattern.test(input)) {
    return {
      isValid: false,
      errorType: 'format',
      errorMessage: i18n.t("register_form_mm.error.messages.tenant_code_format") as string
    };
  }

  // NG文字列と一致するかチェック
  for (const ngCode of NG_TENANT_CODES) {
    if (input.includes(ngCode)) {
      return {
        isValid: false,
        errorType: 'ng_word',
        ngWord: ngCode,
        errorMessage: i18n.t("register_form_mm.error.messages.tenant_code_ng_word", { ngWord: ngCode }) as string
      };
    }
  }

  // NG正規表現と一致するかチェック
  for (const ng_tenant_pattern of NG_TENANT_CODE_PATTERNS) {
    if (ng_tenant_pattern.test(input)) {
      // 正規表現のパターンから、どの部分がNGなのかを抽出
      const patternStr = ng_tenant_pattern.toString();
      let ngWord = input;
      // パターンから具体的なNG文字列を抽出する汎用的な方法
      // 例: /^.*miitel.*$/ → "miitel"
      const literalMatch = patternStr.match(/\.\*([a-z0-9-]+)\.\*/);
      if (literalMatch) {
        ngWord = literalMatch[1];
      }
      // 例: /^ogn.*$/ → "ogn"
      else if (patternStr.match(/^\^([a-z0-9-]+)\.\*/)) {
        const prefixMatch = patternStr.match(/^\^([a-z0-9-]+)/);
        if (prefixMatch) {
          ngWord = prefixMatch[1];
        }
      }
      // 例: /^.*-api$/ → "-api"
      else if (patternStr.match(/\.\*([a-z0-9-]+)\$/)) {
        const suffixMatch = patternStr.match(/\.\*([a-z0-9-]+)\$/);
        if (suffixMatch) {
          ngWord = suffixMatch[1];
        }
      }

      return {
        isValid: false,
        errorType: 'ng_word',
        ngWord: ngWord,
        errorMessage: i18n.t("register_form_mm.error.messages.tenant_code_ng_word", { ngWord: ngWord }) as string
      };
    }
  }

  return { isValid: true };
};

export const isValidResponseTenantCode = (tenantCode: string): boolean => {
  const NGIncludeTenantCode = ["revcomm", "miitel", "pbx", "api"];
  const NGEquivalentTenantCode = [
    "softphone",
    "e2e",
    "developer",
    "demo",
    "developers",
  ];
  return (
    !NGIncludeTenantCode.some((NGcode) => tenantCode.includes(NGcode)) &&
    !NGEquivalentTenantCode.includes(tenantCode)
  );
};

export const isValidEmail = (email: string) => {
  // メールの形式が正しいかをチェックする正規表現
  const ok_pattern = new RegExp(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
  return ok_pattern.test(email);
};